import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Plus, Trash2, FileSpreadsheet, Check, X, 
  ChevronRight, ChevronLeft, Tag, AlertCircle, 
  Calendar, FolderOpen, Users, DollarSign, History, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { campaignService, portfolioService } from '@/lib/api';

const CampaignManager = ({ onImportComplete }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPortfolioFilter, setSelectedPortfolioFilter] = useState(null);
  
  // Create campaign dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    portfolio_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });
  
  // Bulk import wizard
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [importConfig, setImportConfig] = useState({
    import_mode: 'add_and_update',
    match_field: 'dni',
    phone_accumulation: true,
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  // Import history
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioFilter]);

  const loadData = async () => {
    try {
      const [campaignsRes, portfoliosRes] = await Promise.all([
        campaignService.list(selectedPortfolioFilter),
        portfolioService.list()
      ]);
      setCampaigns(campaignsRes.data);
      setPortfolios(portfoliosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!newCampaign.portfolio_id) {
      toast.error('Debes seleccionar una cartera');
      return;
    }

    try {
      await campaignService.create(newCampaign);
      toast.success('Campaña creada exitosamente');
      setShowCreateDialog(false);
      setNewCampaign({
        name: '',
        portfolio_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear campaña');
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`¿Eliminar la campaña "${campaign.name}" y todos sus ${campaign.debtors_count} clientes?`)) {
      return;
    }

    try {
      const result = await campaignService.delete(campaign.id);
      toast.success(result.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar campaña');
    }
  };

  const handleExportCampaign = async (campaign) => {
    try {
      toast.loading('Exportando clientes...');
      const response = await campaignService.exportCampaign(campaign.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = campaign.name.replace(/[^\w\-]/g, '_');
      link.download = `clientes_${safeName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success(`${campaign.debtors_count} clientes exportados`);
    } catch (error) {
      toast.dismiss();
      if (error.response?.status === 404) {
        toast.error('No hay clientes en esta campaña para exportar');
      } else {
        toast.error('Error al exportar clientes');
      }
    }
  };

  // Import wizard functions
  const startImport = (campaign) => {
    setSelectedCampaign(campaign);
    setImportStep(1);
    setUploadedFile(null);
    setUploadResponse(null);
    setColumnMappings({});
    setImportConfig({
      import_mode: 'add_and_update',
      match_field: 'dni',
      phone_accumulation: true,
      tags: []
    });
    setTagInput('');
    setImportResult(null);
    setShowImportWizard(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      toast.loading('Descargando template...');
      const response = await campaignService.downloadTemplate();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_importacion_clientes.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Template descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al descargar template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) {
      toast.error('Formato no soportado. Use CSV o Excel (.xlsx)');
      return;
    }

    setUploadedFile(file);
    toast.loading('Analizando archivo...');

    try {
      const response = await campaignService.uploadFile(selectedCampaign.id, file);
      toast.dismiss();
      setUploadResponse(response.data);
      
      // Initialize mappings with suggested values
      const initialMappings = {};
      response.data.headers.forEach(header => {
        initialMappings[header] = response.data.suggested_mappings[header] || 'ignore';
      });
      setColumnMappings(initialMappings);
      
      setImportStep(2);
      toast.success(`Archivo analizado: ${response.data.row_count} filas`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    }
  };

  const handleColumnMappingChange = (header, value) => {
    setColumnMappings(prev => ({
      ...prev,
      [header]: value
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !importConfig.tags.includes(tagInput.trim())) {
      setImportConfig(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setImportConfig(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const pollImportStatus = async (importId) => {
    const POLL_INTERVAL = 2000;
    const poll = async () => {
      try {
        const res = await campaignService.getImportStatus(importId);
        const data = res.data;
        setImportProgress(data);

        if (data.status === 'completed' || data.status === 'failed') {
          setImporting(false);
          setImportResult(data);
          setImportStep(4);
          loadData();
          if (onImportComplete) onImportComplete();

          if (data.status === 'failed') {
            toast.error(data.error_message || 'La importación falló');
          } else if (data.error_count > 0) {
            toast.warning(`Importación completada con ${data.error_count} errores`);
          } else {
            toast.success('Importación completada exitosamente');
          }
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
  };

  const executeImport = async () => {
    const mappedFields = Object.values(columnMappings);
    const hasIdentifier = ['dni', 'cuit', 'client_code', 'plate'].some(f => mappedFields.includes(f));
    const hasName = mappedFields.includes('name');

    if (!hasIdentifier) {
      toast.error('Debes mapear al menos un identificador (DNI, CUIT, Código o Dominio)');
      return;
    }
    if (!hasName) {
      toast.error('Debes mapear el campo Nombre');
      return;
    }

    setImporting(true);
    setImportProgress(null);

    try {
      const mappingsArray = Object.entries(columnMappings)
        .filter(([, field]) => field !== 'ignore')
        .map(([file_column, debtor_field]) => ({
          file_column,
          debtor_field
        }));

      const response = await campaignService.executeImport(uploadResponse.import_id, {
        campaign_id: selectedCampaign.id,
        column_mappings: mappingsArray,
        import_mode: importConfig.import_mode,
        match_field: importConfig.match_field,
        phone_accumulation: importConfig.phone_accumulation,
        tags: importConfig.tags.length > 0 ? importConfig.tags : null
      });

      // Backend now returns immediately with status "processing"
      setImportStep(4);
      setImportProgress({
        status: 'processing',
        total_rows: response.data.total_rows,
        processed_rows: 0,
      });
      toast.success('Importación iniciada en segundo plano');

      // Start polling for progress
      pollImportStatus(uploadResponse.import_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar la importación');
      setImporting(false);
    }
  };

  const loadImportHistory = async (campaign) => {
    setSelectedCampaign(campaign);
    setHistoryLoading(true);
    setShowImportHistory(true);

    try {
      const response = await campaignService.listImports(campaign.id);
      setImportHistory(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Campañas</h2>
          <p className="text-slate-600">Gestiona campañas de importación de clientes</p>
        </div>
        <div className="flex gap-3">
          <Select
            value={selectedPortfolioFilter || "all"}
            onValueChange={(v) => setSelectedPortfolioFilter(v === "all" ? null : v)}
          >
            <SelectTrigger className="w-48" data-testid="campaign-portfolio-filter">
              <SelectValue placeholder="Todas las carteras" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las carteras</SelectItem>
              {portfolios.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Campaña
          </Button>
        </div>
      </div>

      {/* Campaigns Grid */}
      {campaigns.length === 0 ? (
        <Card className="p-12 text-center">
          <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No hay campañas</h3>
          <p className="text-slate-500 mb-4">Crea una campaña para comenzar a importar clientes</p>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Campaña
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {campaigns.map(campaign => (
            <Card key={campaign.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`campaign-card-${campaign.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <FolderOpen className="w-3.5 h-3.5" />
                    {campaign.portfolio_name}
                  </p>
                </div>
                <Badge variant={campaign.is_active ? "default" : "secondary"}>
                  {campaign.is_active ? "Activa" : "Inactiva"}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <Users className="w-4 h-4 text-teal-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-900">{campaign.debtors_count}</p>
                  <p className="text-xs text-slate-500">Clientes</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <DollarSign className="w-4 h-4 text-rose-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-900">
                    ${campaign.total_debt?.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || 0}
                  </p>
                  <p className="text-xs text-slate-500">Deuda Total</p>
                </div>
              </div>

              <div className="text-xs text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {campaign.start_date}
                  {campaign.end_date && ` - ${campaign.end_date}`}
                </div>
                {campaign.imports_count > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Upload className="w-3 h-3" />
                    {campaign.imports_count} importaciones
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => startImport(campaign)}
                  data-testid={`import-btn-${campaign.id}`}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Importar
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleExportCampaign(campaign)}
                  disabled={!campaign.debtors_count}
                  data-testid={`export-btn-${campaign.id}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Exportar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadImportHistory(campaign)}
                  data-testid={`history-btn-${campaign.id}`}
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteCampaign(campaign)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid={`delete-campaign-btn-${campaign.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Campaña</DialogTitle>
            <DialogDescription>
              Crea una campaña para organizar la importación de clientes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la campaña *</Label>
              <Input
                data-testid="campaign-name-input"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="Ej: Importación Enero 2026"
              />
            </div>
            <div>
              <Label>Cartera *</Label>
              <Select
                value={newCampaign.portfolio_id}
                onValueChange={(v) => setNewCampaign({...newCampaign, portfolio_id: v})}
              >
                <SelectTrigger data-testid="campaign-portfolio-select">
                  <SelectValue placeholder="Selecciona una cartera" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha de inicio</Label>
                <Input
                  type="date"
                  data-testid="campaign-start-date"
                  value={newCampaign.start_date}
                  onChange={(e) => setNewCampaign({...newCampaign, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Fecha de fin (opcional)</Label>
                <Input
                  type="date"
                  data-testid="campaign-end-date"
                  value={newCampaign.end_date}
                  onChange={(e) => setNewCampaign({...newCampaign, end_date: e.target.value})}
                />
              </div>
            </div>
            <Button 
              onClick={handleCreateCampaign} 
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-create-campaign-btn"
            >
              Crear Campaña
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Wizard Dialog */}
      <Dialog open={showImportWizard} onOpenChange={setShowImportWizard}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard-dialog">
          <DialogHeader>
            <DialogTitle>
              Importar Clientes - {selectedCampaign?.name}
            </DialogTitle>
            <DialogDescription>
              Paso {importStep} de 4
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          <div className="flex gap-1 mb-6">
            {[1, 2, 3, 4].map(step => (
              <div
                key={step}
                className={`h-2 flex-1 rounded-full ${
                  step <= importStep ? 'bg-teal-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {/* Step 1: Upload File */}
          {importStep === 1 && (
            <div className="space-y-4">
              {/* Download Template Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDownloadTemplate}
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar Template Excel
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-teal-500 transition-colors">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-medium text-slate-700 mb-2">Arrastra tu archivo aquí</h3>
                <p className="text-sm text-slate-500 mb-4">o haz clic para seleccionar</p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" className="cursor-pointer" asChild>
                    <span>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Seleccionar Archivo
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-slate-400 mt-4">
                  Formatos soportados: CSV, Excel (.xlsx, .xls)
                </p>
              </div>
              {uploadedFile && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                  <span className="font-medium">{uploadedFile.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {importStep === 2 && uploadResponse && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Archivo:</strong> {uploadResponse.filename} - {uploadResponse.row_count} filas
                </p>
              </div>

              {uploadResponse.has_saved_mappings && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal-600 shrink-0" />
                  <p className="text-sm text-teal-800">
                    Se aplicó el mapeo guardado de la importación anterior. Verificá que sea correcto.
                  </p>
                </div>
              )}

              <h3 className="font-medium">Mapear Columnas</h3>
              <p className="text-sm text-slate-500">
                Indica a qué campo corresponde cada columna del archivo
              </p>

              <div className="max-h-[400px] overflow-y-auto space-y-3">
                {uploadResponse.headers.map(header => (
                  <div key={header} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{header}</p>
                      <p className="text-xs text-slate-500">
                        Ej: {uploadResponse.sample_rows[0]?.[header] || '-'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <Select
                      value={columnMappings[header] || 'ignore'}
                      onValueChange={(v) => handleColumnMappingChange(header, v)}
                    >
                      <SelectTrigger className="w-48" data-testid={`mapping-${header}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {uploadResponse.debtor_fields.map(field => (
                          <SelectItem key={field.value} value={field.value}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setImportStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Atrás
                </Button>
                <Button onClick={() => setImportStep(3)} className="bg-teal-600 hover:bg-teal-700">
                  Continuar
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Import Options */}
          {importStep === 3 && (
            <div className="space-y-6">
              <div>
                <Label className="text-base font-medium">Modo de importación</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[
                    { value: 'add_only', label: 'Solo agregar', desc: 'Crea nuevos, ignora existentes' },
                    { value: 'update_only', label: 'Solo actualizar', desc: 'Actualiza existentes, ignora nuevos' },
                    { value: 'add_and_update', label: 'Agregar y actualizar', desc: 'Crea nuevos y actualiza existentes' },
                    { value: 'update_contacts', label: 'Actualizar contactos', desc: 'Solo actualiza teléfono y email' }
                  ].map(mode => (
                    <div
                      key={mode.value}
                      onClick={() => setImportConfig(prev => ({ ...prev, import_mode: mode.value }))}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        importConfig.import_mode === mode.value
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`mode-${mode.value}`}
                    >
                      <p className="font-medium text-sm">{mode.label}</p>
                      <p className="text-xs text-slate-500">{mode.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base font-medium">Campo de coincidencia</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Campo usado para identificar clientes existentes
                </p>
                <Select
                  value={importConfig.match_field}
                  onValueChange={(v) => setImportConfig(prev => ({ ...prev, match_field: v }))}
                >
                  <SelectTrigger data-testid="match-field-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dni">DNI</SelectItem>
                    <SelectItem value="cuit">CUIT</SelectItem>
                    <SelectItem value="client_code">Código de Cliente</SelectItem>
                    <SelectItem value="plate">Dominio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Teléfonos y emails adicionales</Label>
                  <p className="text-sm text-slate-500">
                    Las columnas Teléfono 2/3 y Email 2/3 se suman a la ficha CRM del titular sin borrar los contactos que ya tiene
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-base font-medium">Etiquetas</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Etiquetas que se asignarán a todos los clientes importados
                </p>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Agregar etiqueta..."
                    onKeyDown={(e) => e.key === 'Enter' && addTag()}
                    data-testid="tag-input"
                  />
                  <Button variant="outline" onClick={addTag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {importConfig.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      <Tag className="w-3 h-3" />
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-rose-600"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setImportStep(2)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Atrás
                </Button>
                <Button 
                  onClick={executeImport} 
                  disabled={importing}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="execute-import-btn"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Ejecutar Importación
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Progress & Results */}
          {importStep === 4 && (
            <div className="space-y-6">
              {/* Still processing - show progress */}
              {importProgress && importProgress.status === 'processing' && (
                <>
                  <div className="text-center py-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-teal-100">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">Procesando importación...</h3>
                    <p className="text-slate-500 mt-1">No cierres esta ventana</p>
                  </div>

                  {/* Progress bar */}
                  <div>
                    <div className="flex justify-between text-sm text-slate-600 mb-1">
                      <span>{importProgress.processed_rows || 0} de {importProgress.total_rows} filas</span>
                      <span>
                        {importProgress.total_rows > 0
                          ? Math.round(((importProgress.processed_rows || 0) / importProgress.total_rows) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className="bg-teal-600 h-3 rounded-full transition-all duration-500"
                        style={{
                          width: `${importProgress.total_rows > 0
                            ? Math.round(((importProgress.processed_rows || 0) / importProgress.total_rows) * 100)
                            : 0}%`
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{importProgress.created_count || 0}</p>
                      <p className="text-xs text-slate-500">Creados</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{importProgress.updated_count || 0}</p>
                      <p className="text-xs text-slate-500">Actualizados</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-600">{importProgress.skipped_count || 0}</p>
                      <p className="text-xs text-slate-500">Omitidos</p>
                    </div>
                    <div className="text-center p-3 bg-rose-50 rounded-lg">
                      <p className="text-2xl font-bold text-rose-600">{importProgress.error_count || 0}</p>
                      <p className="text-xs text-slate-500">Errores</p>
                    </div>
                  </div>
                </>
              )}

              {/* Completed or failed - show final results */}
              {importResult && (importResult.status === 'completed' || importResult.status === 'failed') && (
                <>
                  <div className="text-center py-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      importResult.status === 'failed' ? 'bg-rose-100' :
                      importResult.error_count === 0 ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {importResult.status === 'failed' ? (
                        <X className="w-8 h-8 text-rose-600" />
                      ) : importResult.error_count === 0 ? (
                        <Check className="w-8 h-8 text-green-600" />
                      ) : (
                        <AlertCircle className="w-8 h-8 text-amber-600" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {importResult.status === 'failed' ? 'Importación Fallida' :
                       importResult.error_count === 0 ? 'Importación Completada' : 'Completada con Advertencias'}
                    </h3>
                  </div>

                  {importResult.status === 'failed' && importResult.error_message && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                      <p className="text-sm text-rose-700">{importResult.error_message}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-900">{importResult.total_rows || importResult.processed_rows || 0}</p>
                      <p className="text-xs text-slate-500">Total Filas</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{importResult.created_count}</p>
                      <p className="text-xs text-slate-500">Creados</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{importResult.updated_count}</p>
                      <p className="text-xs text-slate-500">Actualizados</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-600">{importResult.skipped_count}</p>
                      <p className="text-xs text-slate-500">Omitidos</p>
                    </div>
                  </div>

                  {importResult.error_count > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                      <h4 className="font-medium text-rose-800 mb-2">
                        {importResult.error_count} errores encontrados
                      </h4>
                      <ul className="text-sm text-rose-700 space-y-1 max-h-40 overflow-y-auto">
                        {importResult.errors?.map((error) => (
                          <li key={error}>&bull; {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={() => setShowImportWizard(false)}
                    className="w-full"
                    data-testid="close-import-result-btn"
                  >
                    Cerrar
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={showImportHistory} onOpenChange={setShowImportHistory}>
        <DialogContent className="max-w-2xl" data-testid="import-history-dialog">
          <DialogHeader>
            <DialogTitle>Historial de Importaciones - {selectedCampaign?.name}</DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay importaciones registradas</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {importHistory.map(imp => (
                <div key={imp.id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium">{imp.filename}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(imp.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <Badge variant="outline">
                      {imp.import_mode === 'add_only' ? 'Solo agregar' :
                       imp.import_mode === 'update_only' ? 'Solo actualizar' :
                       imp.import_mode === 'update_contacts' ? 'Actualizar contactos' : 'Agregar y actualizar'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    <div>
                      <p className="font-bold text-green-600">{imp.created_count}</p>
                      <p className="text-xs text-slate-500">Creados</p>
                    </div>
                    <div>
                      <p className="font-bold text-blue-600">{imp.updated_count}</p>
                      <p className="text-xs text-slate-500">Actualizados</p>
                    </div>
                    <div>
                      <p className="font-bold text-slate-600">{imp.skipped_count}</p>
                      <p className="text-xs text-slate-500">Omitidos</p>
                    </div>
                    <div>
                      <p className="font-bold text-rose-600">{imp.error_count}</p>
                      <p className="text-xs text-slate-500">Errores</p>
                    </div>
                  </div>
                  {imp.tags?.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {imp.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignManager;
